<template>
  <v-app class="app">
    <!-- APP BAR -->
    <app-bar
      :drawer="drawer"
      :options="{
        'clipped-left': true,
        'clipped-right': true,
      }"
    />

    <!-- CONVERSATIONS -->
    <conversation
      :drawer="drawer"
      :conversations="conversations"
      :conversation="conversation"
      :paginations="paginations.conversation"
      :window-height="windowHeight"
      @selectConversation="selectConversationEvent"
      @getMoreConversations="getMoreConversations"
      @filterConversationsApply="filterConversationsApply"
    />

    <v-main class="pb-0 mb-0">
      <v-container fluid class="px-8 pt-5 pb-0">
        <v-card flat class="messages elevation-1 py-0 mx-auto">
          <!-- HEADER -->
          <conversation-header
            :conversation="conversation"
            :loading="loading"
            :forms="items.forms"
            :form="form"
            :is-participant="isParticipant"
            @join="joinConversation"
            @refresh="reloadMessages"
            @open-drawer="drawer.left = 1"
            @toggle-info="drawer.right = !drawer.right"
            @update:form="Object.assign(form, $event)"
          />

          <v-divider />

          <!-- MESSAGES -->
          <div
            :style="{
              height: windowHeight + 'px',
            }"
          >
            <template v-if="conversation">
              <message
                ref="messages"
                :conversation="conversation"
                :messages="messages"
                :paginations="paginations.message"
                :window-height="windowHeight"
                @getMoreMessages="getMoreMessages"
                @deleteMessage="deleteMessage"
              />

              <!-- COMPOSER -->
              <template
                v-if="$permission.$can('message.send', ['user', 'service'])"
              >
                <v-divider />

                <conversation-composer
                  :message.sync="form.message"
                  :disabled="!isParticipant"
                  :uploading="loading.upload"
                  :upload-progress="uploadProgress"
                  :max-length="channelLengthLimits.maxLength"
                  :length-info="channelLengthLimits.infos"
                  @send="sendMessage"
                  @add-media="addMediaMessage"
                />
              </template>
            </template>
          </div>
        </v-card>
      </v-container>
    </v-main>

    <!-- INFOS -->
    <infos
      v-if="conversation"
      :drawer="drawer"
      :contact-info="conversation.contactInfo"
      :window-height="windowHeight"
      :conversation-id="conversation.id"
      :service-id="conversation.channel.service.id"
      :identifier="identifier"
      @updateContactInfo="updateContactInfo"
      @selectConversation="selectConversationEvent"
      @updateIdentifier="updateIdentifier"
    />
  </v-app>
</template>

<script>
import AppBar from "../../../components/layout/app-bar.vue";
import Conversation from "./conversation.vue";
import Message from "../message/message.vue";
import Infos from "../infos.vue";
import ConversationHeader from "../conversation/conversation-header.vue";
import ConversationComposer from "../conversation/conversation-composer.vue";

import { mapGetters, mapActions } from "vuex";

import FilterMixin from "../../../mixins/filter.js";

import {
  createDefaultMeta,
  createMessagePagination,
  createConversationPagination,
} from "../../../constants/index.js";

import {
  normalizeConversation,
  normalizeConversations,
  getConversationContactId,
  getMessageNotificationContent,
} from "./helpers/conversation.helpers";

export default {
  name: "ConversationPage",

  mixins: [FilterMixin],

  components: {
    AppBar,
    Conversation,
    Message,
    Infos,
    ConversationHeader,
    ConversationComposer,
  },

  data() {
    return {
      drawer: {
        left: true,
        right: true,
      },

      identifier: null,

      conversations: [],
      conversation: null,
      messages: [],
      participants: [],
      notifications: [],

      items: {
        forms: [],
      },

      form: {
        message: null,
        formsId: null,
        contactId: null,
      },

      loading: {
        action: false,
        forms: false,
        upload: false,
        conversation: false,
      },

      uploadProgress: 0,

      windowHeight: null,
      windowWidth: null,

      filters: {
        conversation: [],
      },

      paginations: {
        message: createMessagePagination(),
        conversation: createConversationPagination(),
      },

      meta: createDefaultMeta(),
    };
  },

  mounted() {
    this.updateWindowSize();

    window.addEventListener("resize", this.updateWindowSize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateWindowSize);
  },

  sockets: {
    "messages/new": function (data) {
      this.handleNewMessage(data);
    },

    "messages/last": function (response) {
      this.handleConversationList(response);
    },

    "messages/list": function (response) {
      this.handleMessageList(response);
    },

    "messages/send": function (data) {
      this.handleMessageSent(data);
    },

    "messages/delete": function (data) {
      this.handleMessageDeleted(data);
    },

    "messages/tracking": function (data) {
      this.handleMessageTracking(data);
    },

    "conversations/new": function (data) {
      this.addConversationToTop(data.message);
      this.addNotification(data.message);
    },

    "conversations/get-participants": function (data) {
      if (data.status === "success") {
        this.participants = data.participants;
      }
    },

    "conversations/open": function (data) {
      this.handleConversationOpened(data);
    },

    "conversations/joined": function (data) {
      this.handleConversationJoined(data);
    },

    "conversation/opened": function (data) {
      this.handleConversationJoined(data);
    },

    "conversations/read": function (data) {
      if (data.status === "success") {
        this.updateConversationById(data.conversation.id, data.conversation);
      }
    },

    "conversations/readed": function (data) {
      this.updateConversationById(data.conversation.id, data.conversation);
    },

    authenticated: function () {
      this.getConversations();
      this.getNewMessagesAfterReconnection();
    },
  },

  computed: {
    conversationHeader() {
      if (!this.conversation) {
        return {
          title: this.$t("chat.infos.title"),
          subtitle: this.$t("chat.infos.subtitle"),
        };
      }

      return {
        title: this.$t("chat.infos.title1", {
          contactId: this.conversation.sourceId,
          channel: this.conversation.channel.type,
        }),

        subtitle: this.$t("chat.infos.subtitle1", {
          service: this.conversation.channel.service.name,
        }),
      };
    },

    isParticipant() {
      if (!this.participants.length) {
        return false;
      }

      return this.participants.some(
        (participant) =>
          participant.participantId === this.$auth.user().username,
      );
    },

    channelLengthLimits() {
      const result = {
        maxLength: null,
        maxLengthPart: null,
        infos: "",
      };

      if (!this.conversation) {
        return result;
      }

      const channelType = this.conversation.channel.type;

      const limits = this._.at(this.channelSettings, `limits.${channelType}`);

      if (!limits.length) {
        return result;
      }

      const limit = limits[0];

      const messageLength = this.form.message ? this.form.message.length : 0;

      if (limit.max_characters > 0) {
        result.maxLength = limit.max_characters;

        result.infos = ` ${this.$utils.pad(messageLength, 2)} / ${String(
          limit.max_characters,
        )}`;
      }

      if (limit.max_characters_part > 0) {
        result.maxLengthPart = limit.max_characters_part;

        result.infos = `${this.$utils.pad(
          Math.ceil(messageLength / limit.max_characters_part),
          2,
        )} part(s) | ${result.infos}`;
      }

      return result;
    },

    ...mapGetters({
      socketIsAuthenticated: "socketIsAuthenticated",

      channelSettings: "settings/channels",
    }),
  },

  watch: {
    "$vuetify.breakpoint.xsOnly"(value) {
      if (value) {
        this.drawer.left = true;
      }
    },

    "conversation.channel.service.id"(value) {
      this.setFormsItems(value);
    },

    identifier(value) {
      if (value) {
        this.form.contactId = value;
      } else {
        this.form.contactId = this.conversation
          ? this.conversation.sourceId
          : null;
      }
    },
  },

  methods: {
    ...mapActions({
      notify: "notification/notify",
      closeNotify: "notification/close",
      request: "request",
    }),

    updateWindowSize() {
      this.windowHeight = window.innerHeight - 170;

      this.windowWidth = window.innerWidth;
    },

    /*
     * Get conversations list
     */
    getConversations() {
      const qb = this.$requestBuilder.create();

      if (this.filters.conversation.length) {
        this.filters.conversation.forEach((filter) => qb.setFilter(filter));
      }

      qb.setPage(this.paginations.conversation.page);

      qb.setLimit(this.paginations.conversation.limit);

      this.$socket.emit("messages/last", {
        query: qb.query(),
      });
    },

    getMoreConversations($state) {
      this.paginations.conversation.page += 1;
      this.paginations.conversation.state = $state;

      this.getConversations();
    },

    setConversations(datas) {
      this.conversations = normalizeConversations(datas);
    },

    addConversations(datas) {
      datas.forEach((data) => {
        const conversation = normalizeConversation(data);

        if (!conversation) return;

        const index = this.conversations.findIndex(
          (item) => item.id === conversation.id,
        );

        if (index > -1) {
          this.updateConversationById(conversation.id, conversation, index);
        } else {
          this.conversations.push(conversation);
        }
      });
    },

    addConversationToTop(data) {
      const conversation = normalizeConversation(data);

      if (conversation) {
        this.conversations.unshift(conversation);
      }
    },

    addConversationToBottom(data) {
      const conversation = normalizeConversation(data);

      if (conversation) {
        this.conversations.push(conversation);
      }
    },

    selectConversationEvent(conversation) {
      this.form.message = null;

      this.selectConversation(conversation);
    },

    selectConversation(conversation) {
      this.conversation = conversation;

      this.paginations.message = createMessagePagination();

      this.messages = [];

      this.participants = [];

      this.getParticipants();

      this.form.contactId = getConversationContactId(conversation, this.$utils);
    },

    filterConversationsApply(form) {
      this.filters.conversation = this.filterApply(form);

      this.paginations.conversation.page = 0;

      this.loading.conversation = true;

      this.getConversations();
    },

    /*
     * Get message list
     */
    getMessages() {
      if (!this.conversation) return;

      const qb = this.$requestBuilder.create();

      qb.setFilter({
        field: "conversation.id",
        operator: "$eq",
        value: this.conversation.id,
      });

      qb.setPage(this.paginations.message.page);

      qb.setLimit(this.paginations.message.limit);

      this.$socket.emit("messages/list", {
        query: qb.query(),
      });
    },

    reloadMessages() {
      this.paginations.message.page = 1;

      this.getMessages();
    },

    getMoreMessages($state) {
      this.paginations.message.page += 1;

      this.paginations.message.state = $state;

      this.getMessages();
    },

    getNewMessagesAfterReconnection() {
      if (!this.conversation) return;

      this.paginations.message.page = 1;

      this.getMessages();
    },

    setMessages(messages) {
      this.messages = messages.reverse();

      this.scrollToBottomMessage();
      this.markConversationToRead();
    },

    addMessages(messages) {
      messages.forEach((message) => {
        const index = this.messages.findIndex((item) => item.id === message.id);

        if (index > -1) {
          this.updateMessageById(message.id, message, index);
        } else {
          this.messages.unshift(message);
        }
      });
    },

    addMessageToTop(message) {
      this.messages.unshift(message);
    },

    addMessageToBotom(message) {
      this.messages.push(message);
    },

    updateMessageById(id, data, index = null) {
      const findIndex =
        index !== null
          ? index
          : this.messages.findIndex((message) => message.id === id);

      if (findIndex < 0) return;

      const message = Object.assign(this.messages[findIndex], data, {
        loading: false,
      });

      this.$set(this.messages, findIndex, {
        ...message,
      });
    },

    deleteMessage(message) {
      if (!message?.id) return;

      this.$socket.emit("messages/delete", {
        messageId: message.id,
        conversationId: this.conversation.id,
      });
    },

    /*
     * Send new message
     */
    sendMessage() {
      if (!this.canSendMessage()) {
        return;
      }

      const message = this.buildOutgoingMessage();
      this.emitOutgoingMessage(message);
      this.addMessageToBotom(message);
      this.addLastMessageConversation(message);
      this.scrollToBottomMessage();
      this.form.message = null;
      this.meta = createDefaultMeta();
    },

    canSendMessage() {
      if (this.meta.type === "TEXT" && !this.form.message) {
        return false;
      }

      if (this.form.message && this.form.message.length > 1600) {
        return false;
      }

      return true;
    },

    buildOutgoingMessage() {
      const messageId = this.$moment().format("Vx");

      const contentValue =
        this.form.message && this.form.message.trim().length
          ? this.form.message
          : " ";

      return {
        id: messageId,
        content: contentValue,
        createdAt: this.$moment().toISOString(),
        conversation: this.conversation,
        direction: "OUT",
        meta: {
          ...this.meta,
        },
        loading: true,
        authorId: this.$auth.user().username,
      };
    },

    emitOutgoingMessage(message) {
      this.$socket.emit("messages/send", {
        conversationId: this.conversation.id,
        content: message.content,
        meta: message.meta,
        identifier: message.id,
      });
    },

    /*
     * Add new media to conversation
     */
    async addMediaMessage(data) {
      this.notify({
        status: false,
      });

      const files = data.files;

      if (!files || !files.length) {
        console.warn("Aucun fichier à envoyer.");

        return;
      }

      this.meta.type = data.type;

      const messageId = this.conversation.message
        ? this.conversation.message.id
        : this.conversation.id;

      const formData = new FormData();

      Array.from(files).forEach((file) => {
        formData.append("media", file);
      });

      this.loading.upload = true;
      this.uploadProgress = 0;

      try {
        const response = await this.request({
          url: `upload/message.media/${messageId}`,
          method: "post",
          data: formData,

          headers: {
            "Content-Type": "multipart/form-data",
          },

          onUploadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
            }
          },

          messages: {
            404: this.$t("medias.errors.M101"),

            422: (error) => {
              const code = error?.code || error?.response?.data?.code;

              return this.$t("medias.errors." + code);
            },
          },
        });

        const currentFile = files[0];

        this.meta.extra = {
          media_id: response.data?.id || "",

          media_name: currentFile?.name || "",

          document: currentFile?.name || "",
        };

        this.sendMessage();
      } catch (error) {
        this.$emit("errors", error);
      } finally {
        this.loading.upload = false;
        this.uploadProgress = 0;
      }
    },

    /**
     * Join conversation
     */
    joinConversation() {
      if (!this.conversation) {
        return;
      }

      this.loading.action = true;

      this.$socket.emit("conversations/open", {
        conversationId: this.conversation.id,
      });
    },

    getParticipants() {
      if (!this.conversation) {
        return;
      }

      this.$socket.emit("conversations/get-participants", {
        conversationId: this.conversation.id,
      });
    },

    markConversationToRead() {
      if (!this.conversation) return;

      if (!this.conversation.totalMessageUnread) {
        return;
      }

      this.$socket.emit("conversations/read", {
        conversationId: this.conversation.id,
      });
    },

    addParticipant(data) {
      this.participants.push(data);
    },

    /**
     * Update conversation
     */
    updateConversationById(
      id,
      data,
      index = null,
      path = null,
      moveToTop = false,
    ) {
      const findIndex =
        index !== null
          ? index
          : this.conversations.findIndex(
              (conversation) => conversation.id === id,
            );

      if (findIndex < 0) {
        return;
      }

      if (!path) {
        const conversation = Object.assign(this.conversations[findIndex], data);

        this.$set(this.conversations, findIndex, conversation);
      } else {
        this.$utils.setPropertyValue(this.conversations[findIndex], path, data);
      }

      if (moveToTop) {
        const toMove = this.conversations.splice(findIndex, 1);

        this.conversations.unshift(...toMove);
      }
    },

    addLastMessageConversation(message) {
      if (!message?.conversation) {
        return;
      }

      const conversationId = message.conversation.id;

      const index = this.conversations.findIndex(
        (conversation) => conversation.id === conversationId,
      );

      if (index < 0) return;

      const conversation = this.conversations[index];

      conversation.message = message;

      this.$set(this.conversations, index, conversation);
    },

    updateContactInfo(data) {
      if (this.conversation && this.conversation.id === data.conversationId) {
        this.$set(this.conversation, "contactInfo", data.contactInfo);

        return;
      }

      this.updateConversationById(
        data.conversationId,
        data.contactInfo,
        null,
        "contactInfo",
      );
    },

    updateIdentifier(identifier) {
      this.identifier = identifier;
    },

    /*
     * Add new notification
     */
    addNotification(message) {
      if (!message) return;

      const conversationId = message.conversation?.id || message.conversationId;

      if (!conversationId) {
        return;
      }

      const exists = this.notifications.some(
        (notification) => notification.id === message.id,
      );

      if (exists) {
        return;
      }

      this.notifications.push({
        id: message.id,

        conversationId,

        contactId:
          message.conversation?.sourceId || message.contactId || "Contact",

        channel: message.conversation?.channel || {
          type: "default",
          service: {
            name: "Omnicanal",
          },
        },

        meta: message.meta || {
          type: "TEXT",
        },

        content: getMessageNotificationContent(message, this.$i18n),
      });

      //this.notifyNewMessage(message);
    },

    /*
     * Set form
     */
    async setFormsItems(serviceId) {
      this.items.forms = [];

      if (!serviceId) {
        return;
      }

      this.loading.forms = true;

      try {
        const response = await this.request({
          url: `forms.items?filter=` + `service.id||$eq||${serviceId}`,
        });

        this.items.forms = response.data;
      } catch (error) {
        //
      } finally {
        this.loading.forms = false;
      }
    },

    /*
     * Handle new message
     */
    handleNewMessage(data) {
      if (!data?.message) {
        return;
      }

      const { conversation, ...message } = data.message;

      this.updateConversationById(
        conversation.id,
        {
          ...conversation,
          message,
        },
        null,
        null,
        true,
      );

      if (this.conversation && conversation.id === this.conversation.id) {
        this.addMessageToBotom(data.message);

        this.markConversationToRead();

        this.scrollToBottomMessage();
      } else {
        this.addNotification(data.message);
      }

      //this.notifyNewMessage(data.message);
    },

    /**
     *
     * @param response
     */
    handleConversationList(response) {
      this.loading.conversation = false;

      if (response.status !== "success") {
        const state = this.paginations.conversation.state;

        if (state) {
          if (response.code === "E403") {
            state.complete();
          } else {
            state.error();
          }
        }

        return;
      }

      const messages = response.messages;

      if (messages.page === 1) {
        this.setConversations(messages.data);
      } else {
        this.addConversations(messages.data);
      }

      const state = this.paginations.conversation.state;

      if (!state) return;

      if (this.paginations.conversation.page < messages.pageCount) {
        this.$nextTick(() => state.loaded());
      } else {
        state.complete();
      }
    },

    /**
     *
     * @param response
     */
    handleMessageList(response) {
      if (response.status !== "success") {
        const state = this.paginations.message.state;

        if (state) {
          if (response.code === "E403") {
            state.complete();
          } else {
            state.error();
          }
        }

        return;
      }

      const messages = response.messages;

      if (messages.page === 1) {
        this.setMessages(messages.data);
      } else {
        this.addMessages(messages.data.reverse());
      }

      const state = this.paginations.message.state;

      if (!state) return;

      if (this.paginations.message.page < messages.pageCount) {
        this.$nextTick(() => state.loaded());
      } else {
        state.complete();
      }
    },

    /**
     *
     * @param data
     */
    handleMessageSent(data) {
      const { status, identifier } = data;

      if (status === "success") {
        const { conversation, ...message } = data.message;

        this.updateMessageById(identifier, data.message);

        this.updateConversationById(
          conversation.id,
          {
            ...conversation,
            message,
          },
          null,
          null,
          true,
        );

        return;
      }

      this.updateMessageById(identifier, {
        id: identifier,
        status: "FAILED",
        error: data.description,
      });
    },

    /**
     *
     * @param data
     */
    handleMessageDeleted(data) {
      if (data.status === "success") {
        const messageId = data.messageId || data.id;

        this.messages = this.messages.filter(
          (message) => message.id !== messageId,
        );

        return;
      }

      this.notify({
        message: this.$t("error_occured"),
      });
    },

    /**
     *
     * @param data
     */
    handleMessageTracking(data) {
      const { conversation, ...message } = data.message;

      this.updateMessageById(message.id, data.message);

      this.updateConversationById(conversation.id, {
        ...conversation,
        message,
      });
    },

    /**
     *
     * @param data
     */
    handleConversationOpened(data) {
      this.loading.action = false;

      if (data.status !== "success") {
        const code = data.code;

        const message =
          code === "E403" ? "conversation.errors.open.E101" : "error_occured";

        this.notify({
          message: this.$t(message),
        });

        return;
      }

      this.addParticipant(data.participant);

      this.addLastMessageConversation(data.message);

      if (
        this.conversation &&
        this.conversation.id === data.message.conversation.id
      ) {
        this.addMessageToBotom(data.message);
      }
    },

    handleConversationJoined(data) {
      this.addParticipant(data.participant);

      this.addLastMessageConversation(data.message);

      if (
        this.conversation &&
        this.conversation.id === data.message.conversation.id
      ) {
        this.addMessageToBotom(data.message);
      }
    },

    scrollToBottomMessage() {
      if (this.$refs.messages) {
        this.$refs.messages.scrollToBottomMessage();
      }
    },

    /* notifyNewMessage(message) {
      console.log("ddd", message);
    }, */
  },
};
</script>

<style lang="scss">
.conversation {
  overflow-y: hidden;

  .v-navigation-drawer {
    z-index: 1;
  }

  .messages {
    &.screen {
      margin-left: 300px;
    }

    &-content {
      height: 365px;
    }

    &-list-wrapper {
      height: 300px;
    }
  }

  &-list-wrapper {
    height: 425px;
  }
}
</style>
